import {
  Address,
  BASE_FEE,
  Contract,
  rpc,
  scValToNative,
  TimeoutInfinite,
  Transaction,
  TransactionBuilder,
} from "@stellar/stellar-sdk";
import { StellarToken } from "../../core/types/stellar";
import { TokenWithBalance } from "../../core/types/tokens";
import { getStellarNetwork } from "../internal/stellarService";

export class StellarRpcClient {
  private server: rpc.Server;

  constructor(rpcUrl: string) {
    this.server = new rpc.Server(rpcUrl);
  }

  /**
   * Returns the balance of a Stellar Contract Token. This is different from an Issued Token.
   *
   * With Contract Tokens, we need to call the .balance method on the token contract
   * and simulate the transaction to get the balance.
   */
  async getBalance(
    userAddress: string,
    tokenAddress: string,
    chainId: string
  ): Promise<string> {
    const account = await this.server.getAccount(userAddress);

    const network = getStellarNetwork(chainId);

    if (network == null) {
      throw new Error(`No Stellar network found for chainId ${chainId}`);
    }

    const txBuilder = new TransactionBuilder(account, {
      fee: (BigInt(BASE_FEE) * BigInt(2)).toString(),
      networkPassphrase: network,
    });

    const contract = new Contract(tokenAddress);

    const tx = txBuilder
      .addOperation(
        contract.call("balance", new Address(userAddress).toScVal())
      )
      .setTimeout(TimeoutInfinite)
      .build();

    const simulateTxResponse = await this.server.simulateTransaction(tx);

    if ("error" in simulateTxResponse) {
      const isNoBalanceError =
        /trying to get non-existing value for contract instance|trustline entry is missing for account/.test(
          simulateTxResponse.error
        );

      // If the error message indicates that the user has no balance just return 0
      // We don't want to spam with this error as it's pretty common
      if (isNoBalanceError) {
        return "0";
      }

      throw new Error(
        `Failed to fetch balance. RPC response: ${simulateTxResponse.error}`
      );
    }

    if ("result" in simulateTxResponse && simulateTxResponse.result != null) {
      const native = scValToNative(simulateTxResponse.result.retval);

      return native.toString();
    }

    throw new Error("Failed to fetch balance");
  }

  async getAllBalances(
    userAddress: string,
    tokens: StellarToken[]
  ): Promise<Array<TokenWithBalance>> {
    const balancePromises = tokens.map((token) => {
      return this.getBalance(
        userAddress,
        token.chainAssetConfig.stellar.contractAddress,
        token.chainId
      );
    });

    const results = await Promise.allSettled(balancePromises);

    const balances = results.map((result) => {
      if (result.status === "fulfilled") {
        return result.value;
      }

      return "0";
    });

    return balances.map((balance, index) => {
      return {
        ...tokens[index],
        balance,
      };
    });
  }

  /**
   * Resolves when the transaction is confirmed, or fails after a timeout.
   */
  async waitForTransaction(
    txHash: string,
    { interval = 2_000, timeout = 40_000 } = {}
  ): Promise<rpc.Api.GetTransactionStatus.SUCCESS> {
    const startTime = Date.now();

    while (true) {
      const result = await this.server.getTransaction(txHash);

      if (result.status === rpc.Api.GetTransactionStatus.NOT_FOUND) {
        if (Date.now() - startTime > timeout) {
          throw new Error(`Transaction ${txHash} not found within timeout`);
        }

        // eslint-disable-next-line @typescript-eslint/no-loop-func
        await new Promise((res) => setTimeout(res, interval));
        continue;
      }

      if (result.status === rpc.Api.GetTransactionStatus.SUCCESS) {
        return result.status;
      }

      throw new Error(`Transaction failed with status: ${result.status}`);
    }
  }

  async isAccountActivated(address: string): Promise<boolean> {
    try {
      await this.getAccount(address);
      return true;
    } catch (error: any) {
      if (error?.message && error.message.includes("Account not found")) {
        return false;
      }

      throw error;
    }
  }

  async getAccount(address: string) {
    return this.server.getAccount(address);
  }

  async sendTransaction(transaction: Transaction) {
    const transactionResponse = await this.server.sendTransaction(transaction);

    if (transactionResponse.status === "ERROR") {
      throw new Error("Error sending transaction");
    }

    return transactionResponse;
  }

  async prepareTransaction(transaction: Transaction) {
    return this.server.prepareTransaction(transaction);
  }
}
